import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { scheduleApi } from '../../api/endpoints.ts';
import { ApiRequestError } from '../../api/client.ts';
import { MoneyInput } from '../money-input.tsx';
import { Alert, Button, Checkbox, Select, TextInput } from '../ui.tsx';
import { formatCents } from '@shared/calculations/pricing.ts';
import { DEFAULT_CURRENCY_CODE } from '@shared/currency.ts';
import { getDateLocale } from '../../i18n/locale.ts';
import { useSession } from '../../state/session.tsx';
import { formatLongDate } from '../../i18n/datetime.ts';
import type { AppointmentSeries } from '@shared/types/index.ts';
import {
  dayPriceGroups,
  dayPriceSummary,
  increasedDayPrices,
  increasedPrice,
  slotDayPrices,
  weekdayShortName,
  withDayPrices,
  type DayPriceIncrease,
} from '@shared/schedule-day-pricing.ts';

/**
 * "From the first of March, this schedule is £X."
 *
 * Every cleaning company raises its rates, and until this existed the only way
 * to do it was to edit the price on the day — which meant it happened by hand,
 * one schedule at a time, or it did not happen.
 *
 * THE PERCENTAGES ARE A CALCULATOR, NOT A UNIT. The buttons fill the price
 * box; what is sent, stored and told to the customer is the resulting amount.
 * Storing "up 6%" would mean re-rounding it on every read, and two visits
 * priced from the same percentage could end up a cent apart.
 *
 * Only one change can be pending at a time, which is why a schedule that
 * already has one shows it (and the way to cancel it) rather than a second
 * form: two dated changes on one schedule is a state nothing could render
 * honestly.
 */

/** The rises a company actually asks for. Anything else is typed into the box. */
const QUICK_PERCENTAGES = [3, 5, 10];

/**
 * A schedule priced by weekday (Mon $180, Wed and Fri $120) is raised as one
 * change: the same amount, or the same percentage, on every weekday's price
 * and on the schedule's own. Each day's result is shown before it is
 * scheduled, and what is sent is those amounts — the same "calculator, not a
 * unit" rule as above.
 */
function DayPricesPreview({
  series,
  increase,
  money,
}: {
  series: AppointmentSeries;
  increase: DayPriceIncrease;
  money: (cents: number) => string;
}) {
  const { t } = useTranslation();
  const currency = series.priceCurrencyCode ?? DEFAULT_CURRENCY_CODE;
  const before = dayPriceGroups(series.daySlots, series.visitPriceCents) ?? [];
  const label = (cents: number | null) => (cents === null ? t('seriesDetail.notBilled') : money(cents));
  return (
    <div className="mb-4">
      <h3 className="mb-1 text-sm font-medium">{t('seriesPriceChange.previewHeading')}</h3>
      <ul className="space-y-1 text-sm" aria-label={t('seriesPriceChange.previewHeading')}>
        {before.map((group) => (
          <li key={group.days.join(',')}>
            {t('seriesPriceChange.previewRow', {
              days: group.days.map((day) => weekdayShortName(day, getDateLocale())).join(', '),
              from: label(group.priceCents),
              to: label(group.priceCents === null ? null : increasedPrice(group.priceCents, increase, currency)),
            })}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PriceChangeCard({
  series,
  writable,
  onChanged,
}: {
  series: AppointmentSeries;
  writable: boolean;
  onChanged: (next: AppointmentSeries) => void;
}) {
  const { t } = useTranslation();
  const { company, currencyCode: sessionCurrency } = useSession();
  // A schedule that has never had a price has no currency of its own, and the
  // server will stamp the COMPANY's on the first one — so that is what the box
  // has to be labeled with. Falling straight through to the default put a
  // dollar sign in front of an amount a GBP company was about to store, and
  // email to its customer, in pounds.
  const currency = series.priceCurrencyCode ?? sessionCurrency;
  const money = (cents: number) => formatCents(cents, currency, getDateLocale());

  const [priceCents, setPriceCents] = useState<number | null>(null);
  const [effectiveOn, setEffectiveOn] = useState('');
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Weekday-priced schedules only: how the rise reaches each day.
  const byWeekday = slotDayPrices(series.daySlots).length > 0;
  const [mode, setMode] = useState<DayPriceIncrease['kind']>('amount');
  const [amountCents, setAmountCents] = useState<number | null>(null);
  const [percentText, setPercentText] = useState('');
  const percent = percentText.trim() === '' ? null : Number(percentText);
  const increase: DayPriceIncrease | null =
    mode === 'amount'
      ? amountCents === null
        ? null
        : { kind: 'amount', cents: amountCents }
      : percent === null || !Number.isFinite(percent)
        ? null
        : { kind: 'percent', basisPoints: Math.round(percent * 100) };
  // A schedule with weekday prices but no price of its own has nothing to add
  // an amount or a percentage to, so each day's new price is typed instead,
  // with the schedule price the server stores alongside them.
  const ownDayPrices = slotDayPrices(series.daySlots);
  const absolute = byWeekday && series.visitPriceCents === null;
  const [dayInputs, setDayInputs] = useState<Record<number, number | null>>({});
  const typedDayPrices = ownDayPrices.map((own) => ({
    dayOfWeek: own.dayOfWeek,
    priceCents: dayInputs[own.dayOfWeek] ?? null,
  }));
  const dayChange = absolute
    ? priceCents !== null && typedDayPrices.every((price) => price.priceCents !== null)
      ? { priceCents, dayPrices: typedDayPrices as Array<{ dayOfWeek: number; priceCents: number }> }
      : null
    : byWeekday && increase && series.visitPriceCents !== null
      ? {
          priceCents: increasedPrice(series.visitPriceCents, increase, currency),
          dayPrices: increasedDayPrices(series.daySlots, increase, currency),
        }
      : null;

  function onFailure(cause: unknown) {
    if (cause instanceof ApiRequestError) {
      setError(cause.message);
      setFieldErrors(cause.fields ?? {});
      return;
    }
    setError(t('seriesPriceChange.failed'));
  }

  const schedulePriceChange = useMutation({
    mutationFn: () =>
      scheduleApi.scheduleSeriesPriceChange(series.id, {
        ...(dayChange ? dayChange : { priceCents: priceCents ?? 0 }),
        effectiveOn,
        notifyCustomer,
      }),
    onMutate: () => {
      setError(null);
      setFieldErrors({});
    },
    onSuccess: (next) => {
      setPriceCents(null);
      setDayInputs({});
      setAmountCents(null);
      setPercentText('');
      setEffectiveOn('');
      onChanged(next);
    },
    onError: onFailure,
  });

  // The request the Schedule button would send, so the preview always shows
  // the email for exactly what is on screen.
  const request = {
    ...(dayChange ? dayChange : { priceCents: priceCents ?? 0 }),
    effectiveOn,
    notifyCustomer,
  };
  const requestKey = JSON.stringify(request);
  const ready = (byWeekday ? dayChange !== null : priceCents !== null) && !!effectiveOn;
  const previewEmail = useMutation({
    mutationFn: (key: string) =>
      scheduleApi
        .previewSeriesPriceChange(series.id, JSON.parse(key) as typeof request)
        .then((email) => ({ key, email })),
    onMutate: () => {
      setError(null);
      setFieldErrors({});
    },
    onError: onFailure,
  });
  const shownPreview = previewEmail.data?.key === requestKey ? previewEmail.data.email : null;
  const emailPreview = notifyCustomer ? (
    <div className="mt-3">
      <Button
        variant="secondary"
        busy={previewEmail.isPending}
        disabled={!ready}
        onClick={() => previewEmail.mutate(requestKey)}
      >
        {t('seriesPriceChange.previewEmail')}
      </Button>
      {shownPreview ? (
        shownPreview.recipient === null ? (
          <p className="mt-3 text-sm text-[color:var(--color-muted)]" role="status">
            {t('seriesPriceChange.previewNoEmail')}
          </p>
        ) : (
          <div
            className="mt-3 rounded-md border border-[color:var(--color-line)] bg-white p-3 text-sm"
            role="region"
            aria-label={t('seriesPriceChange.previewEmailHeading')}
          >
            <h3 className="mb-2 text-sm font-medium">{t('seriesPriceChange.previewEmailHeading')}</h3>
            <p>
              <span className="text-[color:var(--color-muted)]">{t('seriesPriceChange.previewTo')}</span>{' '}
              {shownPreview.recipient}
            </p>
            <p className="mb-2">
              <span className="text-[color:var(--color-muted)]">{t('seriesPriceChange.previewSubject')}</span>{' '}
              {shownPreview.subject}
            </p>
            <p className="whitespace-pre-wrap">{shownPreview.body}</p>
          </div>
        )
      ) : null}
    </div>
  ) : null;

  const cancelPriceChange = useMutation({
    mutationFn: () => scheduleApi.cancelSeriesPriceChange(series.id),
    onMutate: () => setError(null),
    onSuccess: onChanged,
    onError: onFailure,
  });

  const pending = series.scheduledPriceChange;

  return (
    <section className="card mb-6" aria-labelledby="price-change-heading">
      <h2 id="price-change-heading" className="mb-1 text-lg font-semibold">
        {t('seriesPriceChange.heading')}
      </h2>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('seriesPriceChange.description')}</p>

      {error ? (
        <div className="mb-4">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      {pending ? (
        <>
          <Alert tone="info" title={t('seriesPriceChange.pendingTitle')}>
            <p>
              {pending.dayPrices || pending.priceCents === null
                ? t('seriesPriceChange.pendingBodyByDay', {
                    date: formatLongDate(pending.effectiveOn),
                    prices: dayPriceSummary(
                      withDayPrices(series.daySlots, pending.dayPrices),
                      pending.priceCents,
                      (cents) => (cents === null ? t('seriesDetail.notBilled') : money(cents)),
                      getDateLocale(),
                    ),
                  })
                : t('seriesPriceChange.pendingBody', {
                    amount: money(pending.priceCents),
                    date: formatLongDate(pending.effectiveOn),
                  })}
            </p>
            <p className="mt-1 text-sm">
              {pending.customerNotifiedAt
                ? t('seriesPriceChange.customerTold')
                : t('seriesPriceChange.customerNotTold')}
            </p>
          </Alert>
          {writable ? (
            <Button
              className="mt-4"
              variant="ghost"
              busy={cancelPriceChange.isPending}
              onClick={() => cancelPriceChange.mutate()}
            >
              {t('seriesPriceChange.cancelChange')}
            </Button>
          ) : null}
        </>
      ) : !writable ? (
        <p className="text-sm text-[color:var(--color-muted)]">{t('seriesPriceChange.nonePending')}</p>
      ) : absolute ? (
        <>
          <p className="mb-4 text-sm">{t('seriesPriceChange.absoluteDescription')}</p>
          <div className="grid gap-x-4 sm:grid-cols-2">
            {ownDayPrices.map((own) => (
              <MoneyInput
                key={own.dayOfWeek}
                label={t('seriesPriceChange.dayPriceLabel', { day: t(`schedule.dayOfWeek.${own.dayOfWeek}`) })}
                hint={t('seriesPriceChange.newPriceHint', { amount: money(own.priceCents) })}
                valueCents={dayInputs[own.dayOfWeek] ?? null}
                currencyCode={currency}
                onChangeCents={(cents) => setDayInputs((current) => ({ ...current, [own.dayOfWeek]: cents }))}
              />
            ))}
            <MoneyInput
              label={t('seriesPriceChange.otherDaysLabel')}
              hint={t('seriesPriceChange.otherDaysHint')}
              valueCents={priceCents}
              currencyCode={currency}
              {...(fieldErrors.priceCents ? { error: fieldErrors.priceCents } : {})}
              onChangeCents={setPriceCents}
            />
          </div>
          {fieldErrors.dayPrices ? <p className="field-error mb-4">{fieldErrors.dayPrices}</p> : null}
          <TextInput
            label={t('seriesPriceChange.effectiveOnLabel')}
            type="date"
            hint={t('seriesPriceChange.effectiveOnHint')}
            value={effectiveOn}
            {...(fieldErrors.effectiveOn ? { error: fieldErrors.effectiveOn } : {})}
            onChange={(event) => setEffectiveOn(event.target.value)}
          />
          <Checkbox
            label={t('seriesPriceChange.notifyLabel')}
            hint={t('seriesPriceChange.notifyHint')}
            checked={notifyCustomer}
            onChange={(event) => setNotifyCustomer(event.target.checked)}
          />
          {emailPreview}
          <Button
            className="mt-4"
            variant="primary"
            busy={schedulePriceChange.isPending}
            disabled={dayChange === null || !effectiveOn}
            onClick={() => schedulePriceChange.mutate()}
          >
            {t('seriesPriceChange.schedule')}
          </Button>
        </>
      ) : byWeekday ? (
        <>
          <p className="mb-4 text-sm">{t('seriesPriceChange.byDayDescription')}</p>
          <div className="grid gap-x-4 sm:grid-cols-2">
            <Select
              label={t('seriesPriceChange.modeLabel')}
              value={mode}
              options={[
                { value: 'amount', label: t('seriesPriceChange.modeAmount') },
                { value: 'percent', label: t('seriesPriceChange.modePercent') },
              ]}
              onChange={(event) => setMode(event.target.value as DayPriceIncrease['kind'])}
            />
            {mode === 'amount' ? (
              <MoneyInput
                label={t('seriesPriceChange.amountLabel')}
                valueCents={amountCents}
                currencyCode={currency}
                {...(fieldErrors.dayPrices ? { error: fieldErrors.dayPrices } : {})}
                onChangeCents={setAmountCents}
              />
            ) : (
              <TextInput
                label={t('seriesPriceChange.percentLabel')}
                type="number"
                step={0.1}
                inputMode="decimal"
                value={percentText}
                {...(fieldErrors.dayPrices ? { error: fieldErrors.dayPrices } : {})}
                onChange={(event) => setPercentText(event.target.value)}
              />
            )}
          </div>
          {increase ? <DayPricesPreview series={series} increase={increase} money={money} /> : null}
          <TextInput
            label={t('seriesPriceChange.effectiveOnLabel')}
            type="date"
            hint={t('seriesPriceChange.effectiveOnHint')}
            value={effectiveOn}
            {...(fieldErrors.effectiveOn ? { error: fieldErrors.effectiveOn } : {})}
            onChange={(event) => setEffectiveOn(event.target.value)}
          />
          <Checkbox
            label={t('seriesPriceChange.notifyLabel')}
            hint={t('seriesPriceChange.notifyHint')}
            checked={notifyCustomer}
            onChange={(event) => setNotifyCustomer(event.target.checked)}
          />
          {emailPreview}
          <Button
            className="mt-4"
            variant="primary"
            busy={schedulePriceChange.isPending}
            disabled={dayChange === null || !effectiveOn}
            onClick={() => schedulePriceChange.mutate()}
          >
            {t('seriesPriceChange.schedule')}
          </Button>
        </>
      ) : (
        <>
          {/* The quick buttons are disabled with no current price to raise:
              "10% more than nothing" is nothing, and offering it would look
              like the button had failed. */}
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <span className="text-sm text-[color:var(--color-muted)]">{t('seriesPriceChange.quickLabel')}</span>
            {QUICK_PERCENTAGES.map((percent) => (
              <Button
                key={percent}
                variant="ghost"
                disabled={!series.visitPriceCents}
                onClick={() => setPriceCents(Math.round(((series.visitPriceCents ?? 0) * (100 + percent)) / 100))}
              >
                {t('seriesPriceChange.quickPercent', { percent })}
              </Button>
            ))}
          </div>

          <div className="grid gap-x-4 sm:grid-cols-2">
            <MoneyInput
              label={t('seriesPriceChange.newPriceLabel')}
              hint={
                series.visitPriceCents !== null
                  ? t('seriesPriceChange.newPriceHint', { amount: money(series.visitPriceCents) })
                  : t('seriesPriceChange.newPriceHintUnpriced')
              }
              valueCents={priceCents}
              currencyCode={currency}
              {...(fieldErrors.priceCents ? { error: fieldErrors.priceCents } : {})}
              onChangeCents={setPriceCents}
            />
            <TextInput
              label={t('seriesPriceChange.effectiveOnLabel')}
              type="date"
              hint={t('seriesPriceChange.effectiveOnHint')}
              value={effectiveOn}
              {...(fieldErrors.effectiveOn ? { error: fieldErrors.effectiveOn } : {})}
              onChange={(event) => setEffectiveOn(event.target.value)}
            />
          </div>

          <Checkbox
            label={t('seriesPriceChange.notifyLabel')}
            hint={t('seriesPriceChange.notifyHint')}
            checked={notifyCustomer}
            onChange={(event) => setNotifyCustomer(event.target.checked)}
          />
          {emailPreview}

          <Button
            className="mt-4"
            variant="primary"
            busy={schedulePriceChange.isPending}
            disabled={priceCents === null || !effectiveOn}
            onClick={() => schedulePriceChange.mutate()}
          >
            {t('seriesPriceChange.schedule')}
          </Button>
        </>
      )}
    </section>
  );
}
